/**
 * Sentry custom-span helpers.
 *
 * Spans created here attach to the active pageload/navigation transaction and
 * inherit its sampling decision (no extra config). Use a dot-namespaced `op`
 * so spans group cleanly in Explore > Traces (e.g. `app.init.*`, `page.init.*`).
 */
// Named imports (not `import * as`): the namespace form defeats tree-shaking
// and drags the whole SDK surface (replay/feedback) into the vendor chunk.
import {
	setMeasurement as sentrySetMeasurement,
	startSpan as sentryStartSpan,
} from '@sentry/sveltekit';
import {
	ABC_ATTRIBUTES,
	PAYMENT_SDK_STATES,
	RENDERING_MODES,
	getFallbackRootSpan,
	setRouteAttributes,
} from './attributes';
import { PAYMENT_TYPES } from '$lib/utils/constants';

/** Structural span shape passed into `instrument()` callbacks (setAttributes only). */
type TelemetrySpan = {
	setAttributes?(attributes: Record<string, unknown>): void;
};

/** Structural mirror of the SDK's `StartSpanOptions`, for `stampRenderingMode`'s input/output. */
type StartSpanOptionsLike = {
	attributes?: Record<string, unknown>;
	name?: string;
	op?: string;
};

/**
 * Run `fn` inside a Sentry span and return its result.
 * The span ends when `fn` settles. Synchronous and async `fn` are both supported.
 * @template T
 * @param {string} op - Dot-namespaced operation, e.g. `app.init.experiments`.
 * @param {string} name - Human-readable span name.
 * @param {(span: TelemetrySpan | undefined) => T} fn - Work to measure; receives the span so it can set attributes.
 * @param {Record<string, string | number | boolean>} [attributes] - Attributes known up front.
 * @returns {T}
 */
export function instrument<T>(
	op: string,
	name: string,
	fn: (span: TelemetrySpan | undefined) => T,
	attributes?: Record<string, string | number | boolean>
): T {
	// Defensive: if tracing is unavailable (e.g. partially-mocked Sentry in
	// tests), run the work directly so instrumentation never alters behavior.
	if (typeof sentryStartSpan !== 'function') {
		return fn(undefined);
	}
	// Scope-stack loss fallback: when no active span is resolvable but the
	// route transaction is still recording (a navigation started inside a
	// traced async callback loses its scope when that callback settles — see
	// attributes.ts's route-root tracking comment), parent explicitly on the
	// tracked root. Without this, `onlyIfParent` silently drops the span and
	// everything set inside `fn` (child spans, attributes, measurements).
	const fallbackParent = getFallbackRootSpan();
	if (fallbackParent) {
		return sentryStartSpan(
			{ attributes, name, onlyIfParent: true, op, parentSpan: fallbackParent as never },
			fn
		);
	}
	// onlyIfParent: our custom spans are child-only diagnostics. Without it,
	// one that runs after the route transaction idle-ends (e.g. a late
	// experiment init) becomes its own root transaction - noise that pollutes
	// transaction counts and burns quota.
	return sentryStartSpan({ attributes, name, onlyIfParent: true, op }, fn);
}

/**
 * Record that `page.init()` resolved the variant component. Emits an
 * `abc.variant_ready` User Timing mark and a Sentry `time_to_variant_ready`
 * measurement (ms since timeOrigin). This is a **JS-resolution** timestamp:
 * it fires synchronously after the component class is available, before Svelte
 * commits the variant DOM and before the variant is painted. Do not interpret
 * it as "usable page painted" — use `markVariantPainted` for that.
 * @returns {void}
 */
export function markVariantReady(): void {
	sentryMeasurement('time_to_variant_ready');
	markMilestone('variant_ready');
	setRouteAttributes({ [ABC_ATTRIBUTES.VARIANT_READY]: true });
}

/**
 * Record that the prerendered skeleton became visible. Emits an
 * `abc.skeleton_visible` User Timing mark and a Sentry `time_to_skeleton_visible`
 * measurement. Schedule this via `afterPaint` from the skeleton's `onMount` so
 * the mark lands after a paint boundary rather than at assignment time.
 * @returns {void}
 */
export function markSkeletonVisible(): void {
	sentryMeasurement('time_to_skeleton_visible');
	markMilestone('skeleton_visible');
	setRouteAttributes({ [ABC_ATTRIBUTES.SKELETON_VISIBLE]: true });
}

/**
 * Record that the resolved variant has been painted. Emits an `abc.variant_painted`
 * User Timing mark and a Sentry `time_to_variant_painted` measurement. Call this
 * after `markVariantReady`, a Svelte `tick()` (DOM commit), and an `afterPaint`
 * paint boundary so the timestamp reflects a painted variant, not just resolution.
 * @returns {void}
 */
export function markVariantPainted(): void {
	sentryMeasurement('time_to_variant_painted');
	markMilestone('variant_painted');
	measureBetween('variant_paint_lag', 'variant_ready', 'variant_painted');
	emitSkeletonLead();
	setRouteAttributes({ [ABC_ATTRIBUTES.VARIANT_PAINTED]: true });
}

/**
 * Record that a funnel payment form reached technical readiness: the form
 * mounted with its fields rendered and wired. Emits a route-relative
 * `route_time_to_payment_form_interactive` measurement on the active transaction.
 * Call it from the payment form's `onMount` — NOT from a consent-gated signal.
 * (The `payment_form_ready` milestone this replaces fired when `disableForm`
 * flipped false, which the terms-consent checkbox gates, so it measured time
 * to user consent rather than form readiness.) One-shot per route; duplicate
 * calls are suppressed by `markMilestone`.
 * @returns {void}
 */
export function markPaymentFormInteractive(): void {
	markMilestone('payment_form_interactive');
	notePaymentMethodReady(PAYMENT_TYPES.CREDIT_CARD);
}

/**
 * Record that the Apple Pay custom element is defined and a paint frame has
 * run (`customElements.whenDefined('apple-pay-button')` then `afterPaint`).
 * Not an Apple-documented ready event. One-shot per route (`markMilestone`);
 * `{#key}` remounts of the button must not double-fire.
 * @returns {void}
 */
export function markApplePayButtonInteractive(): void {
	markMilestone('apple_pay_button_interactive');
	notePaymentMethodReady(PAYMENT_TYPES.APPLE_PAY);
}

/**
 * Record that PayPal Buttons `onInit` fired. One-shot per route.
 * @returns {void}
 */
export function markPayPalButtonInteractive(): void {
	markMilestone('paypal_button_interactive');
	notePaymentMethodReady(PAYMENT_TYPES.PAYPAL);
}

/**
 * Record how this route found the Apple Pay SDK global (`abc.apple_pay_sdk`) and,
 * unless it never appeared, emit `route_time_to_apple_pay_sdk_ready`.
 *
 * SDK availability is not button readiness: `markApplePayButtonInteractive` still
 * owns that, and this deliberately does not feed `payment_methods_ready`, so the
 * existing readiness metric keeps meaning "the user can tap".
 * @param {PaymentSdkState} state - How the global was resolved.
 * @returns {void}
 */
export function noteApplePaySdkState(state: PaymentSdkState): void {
	notePaymentSdkState('apple_pay', state);
	if (state !== PAYMENT_SDK_STATES.MISSING) markMilestone('apple_pay_sdk_ready');
}

/**
 * Record whether the PayPal SDK was already loaded when this route asked for the
 * service (`abc.paypal_sdk`). No readiness milestone: the state is observed before
 * initialization resolves, so it says nothing about when the SDK became usable —
 * `markPayPalButtonInteractive` remains the readiness signal.
 * @param {PaymentSdkState} state - How the global was resolved (`warm` | `cold`).
 * @returns {void}
 */
export function notePaypalSdkState(state: PaymentSdkState): void {
	notePaymentSdkState('paypal', state);
}

/**
 * Record that the inbound gateway is about to invoke `goto()`. Measures time
 * from gateway page start until handoff is *called* — not `await goto()`,
 * which includes destination load.
 * @returns {void}
 */
export function markInboundRedirectFired(): void {
	markMilestone('inbound_redirect_fired');
}

/**
 * Record that download-app obtained a short-lived token.
 * @returns {void}
 */
export function markTokenReady(): void {
	markMilestone('token_ready');
}

/**
 * Record that the download-app QR (or equivalent badge surface) is in the DOM.
 * @returns {void}
 */
export function markQrVisible(): void {
	markMilestone('qr_visible');
}

const PAYMENT_METHODS = {
	[PAYMENT_TYPES.CREDIT_CARD]: 'form',
	[PAYMENT_TYPES.PAYPAL]: 'paypal',
	[PAYMENT_TYPES.APPLE_PAY]: 'applePay',
} as const;
type PaymentMethodId =
	| typeof PAYMENT_TYPES.CREDIT_CARD
	| typeof PAYMENT_TYPES.PAYPAL
	| typeof PAYMENT_TYPES.APPLE_PAY;
const PAYMENT_METHOD_ORDER = Object.keys(PAYMENT_METHODS) as PaymentMethodId[];

let expectedPaymentMethods: Set<PaymentMethodId> | null = null;
const readyPaymentMethods = new Set<PaymentMethodId>();

/**
 * Declare which payment methods this visit will actually show. Call after
 * `allowPayPal` / `resolveApplePayReadiness()` resolve. Apple Pay is only
 * expected when it will render — not on Android. An empty set does not emit
 * `payment_methods_ready`.
 * @param {{ form: boolean, paypal: boolean, applePay: boolean }} opts - Methods this visit will show.
 * @param {boolean} opts.form - Credit-card form will mount.
 * @param {boolean} opts.paypal - PayPal button will render.
 * @param {boolean} opts.applePay - Apple Pay button will render.
 * @returns {void}
 */
export function declareExpectedPaymentMethods(opts: {
	form: boolean;
	paypal: boolean;
	applePay: boolean;
}): void {
	expectedPaymentMethods = new Set(PAYMENT_METHOD_ORDER.filter((id) => opts[PAYMENT_METHODS[id]]));
	const label = PAYMENT_METHOD_ORDER.filter((method) => expectedPaymentMethods?.has(method)).join(
		'+'
	);
	if (label) {
		setRouteAttributes({ [ABC_ATTRIBUTES.PAYMENT_METHODS_EXPECTED]: label });
	}
	maybeCompletePaymentMethods();
}

/** @param {PaymentMethodId} id - Method that just became interactive. @returns {void} */
function notePaymentMethodReady(id: PaymentMethodId): void {
	readyPaymentMethods.add(id);
	maybeCompletePaymentMethods();
}

/** @returns {void} */
function maybeCompletePaymentMethods(): void {
	if (!expectedPaymentMethods || expectedPaymentMethods.size === 0) return;
	for (const method of expectedPaymentMethods) {
		if (!readyPaymentMethods.has(method)) return;
	}
	markMilestone('payment_methods_ready');
	setRouteAttributes({ [ABC_ATTRIBUTES.PAYMENT_METHODS_READY]: true });
}

/** @returns {void} */
function resetPaymentMethodCoordinator(): void {
	expectedPaymentMethods = null;
	readyPaymentMethods.clear();
}

/** Warm/cold/missing value accepted by the payment-SDK note helpers. */
type PaymentSdkState = (typeof PAYMENT_SDK_STATES)[keyof typeof PAYMENT_SDK_STATES];

/** SDK id -> the `abc.*` attribute that carries its warm/cold state. */
const PAYMENT_SDK_ATTRIBUTES = {
	apple_pay: ABC_ATTRIBUTES.APPLE_PAY_SDK,
	paypal: ABC_ATTRIBUTES.PAYPAL_SDK,
} as const;

type PaymentSdkId = keyof typeof PAYMENT_SDK_ATTRIBUTES;

/** SDKs already stamped on this route. First observation wins. */
const stampedPaymentSdks = new Set<PaymentSdkId>();

/**
 * Stamp an SDK's warm/cold state once per route. Later callers on the same route
 * (a page and its billing form both ask for PayPal) must not overwrite the first
 * observation: by then warmup has had extra time, which would report the route as
 * warmer than it was.
 * @param {PaymentSdkId} sdk - SDK being reported.
 * @param {PaymentSdkState} state - How the global was resolved.
 * @returns {void}
 */
function notePaymentSdkState(sdk: PaymentSdkId, state: PaymentSdkState): void {
	if (stampedPaymentSdks.has(sdk)) return;
	stampedPaymentSdks.add(sdk);
	setRouteAttributes({ [PAYMENT_SDK_ATTRIBUTES[sdk]]: state });
}

/**
 * `browserTracingIntegration`'s `beforeStartSpan` hook: stamps the pageload/navigation
 * root span with rendering-mode + gateway-redirect attributes, never renaming the span
 * (renaming would flip its source to 'custom' and break route-based sampling/grouping).
 * @param {StartSpanOptionsLike} options - SDK-provided startSpan options for the root span.
 * @returns {StartSpanOptionsLike} The same options, with attributes added.
 */
export function stampRenderingMode(options: StartSpanOptionsLike): StartSpanOptionsLike {
	const attributes = { ...options?.attributes };

	attributes[ABC_ATTRIBUTES.RENDERING_MODE] =
		options?.op === 'pageload' ? renderingModeForPageload() : RENDERING_MODES.SPA;

	// Note: gateway pageloads interrupted by their own goto() need no marker here -
	// they're identified by transaction name (/inbound-gateway) and abc.inbound_outcome.

	return { ...options, attributes };
}

/**
 * Rendering mode for a pageload span, based on the fallback-shell marker the adapter
 * stamps into the CSR fallback HTML (see packages/client-static-serve's adapter.ts).
 * @returns {'csr_fallback' | 'prerendered'}
 */
function renderingModeForPageload(): 'csr_fallback' | 'prerendered' {
	if (typeof document === 'undefined') return RENDERING_MODES.PRERENDERED;
	return document.querySelector('meta[name="abc-render-mode"][content="fallback"]')
		? RENDERING_MODES.CSR_FALLBACK
		: RENDERING_MODES.PRERENDERED;
}

/**
 * Run `fn` after the next two animation frames so it executes after the browser
 * has had a chance to paint the current DOM. Returns a cancel function so callers
 * (e.g. a skeleton that unmounts before painting) can abort a pending callback.
 * In non-browser/test environments without `requestAnimationFrame`, runs `fn` on
 * the next microtask so instrumentation stays async-safe without breaking tests.
 * @param {() => void} fn - Work to run after a paint boundary.
 * @returns {() => void}
 */
export function afterPaint(fn: () => void): () => void {
	if (typeof requestAnimationFrame !== 'function') {
		let cancelled = false;
		Promise.resolve().then(() => {
			if (!cancelled) fn();
		});
		return () => {
			cancelled = true;
		};
	}
	let raf1 = 0;
	let raf2 = 0;
	let cancelled = false;
	raf1 = requestAnimationFrame(() => {
		raf2 = requestAnimationFrame(() => {
			if (!cancelled) fn();
		});
	});
	return () => {
		cancelled = true;
		if (raf1) cancelAnimationFrame(raf1);
		if (raf2) cancelAnimationFrame(raf2);
	};
}

/** @param {string} name - User Timing mark name. @returns {void} */
function userTimingMark(name: string): void {
	if (typeof performance !== 'undefined' && typeof performance.mark === 'function') {
		try {
			performance.mark(name);
		} catch {
			/* ignore — mark API must not break rendering */
		}
	}
}

/** @param {string} name - Sentry measurement name. @returns {void} */
function sentryMeasurement(name: string): void {
	if (typeof sentrySetMeasurement === 'function') {
		try {
			const ms =
				typeof performance !== 'undefined' && typeof performance.now === 'function'
					? performance.now()
					: 0;
			// 4th arg: explicit span for the SDK to resolve the root from. It is
			// undefined in normal operation (the SDK's own getActiveSpan default
			// applies) and only set after scope-stack loss — see attributes.ts.
			sentrySetMeasurement(name, ms, 'millisecond', getFallbackRootSpan() as never);
		} catch {
			/* ignore — measurement must not break rendering */
		}
	}
}

/**
 * Emit a Sentry measurement with an explicit value (ms). Used for route-relative
 * durations and derived per-visit durations (e.g. skeleton_lead). No-ops when
 * tracing is unavailable; never throws.
 * @param {string} name - Measurement name.
 * @param {number} ms - Duration in milliseconds.
 * @returns {void}
 */
function sentryMeasurementValue(name: string, ms: number): void {
	if (typeof sentrySetMeasurement !== 'function') return;
	if (typeof ms !== 'number' || !Number.isFinite(ms) || ms < 0) return;
	try {
		// 4th arg: scope-stack loss fallback, undefined in normal operation
		// (same contract as sentryMeasurement above).
		sentrySetMeasurement(name, ms, 'millisecond', getFallbackRootSpan() as never);
	} catch {
		/* ignore — measurement must not break rendering */
	}
}

/* -------------------------------------------------------------------------- *
 * Route-relative journey telemetry
 *
 * The existing mark helpers above emit absolute `time_to_*` measurements
 * (ms since document timeOrigin). Those cannot be subtracted at the aggregate
 * level to compute per-visit durations like skeleton_lead. The helpers below
 * record a per-route start timestamp and emit route-relative measurements and
 * direct derived durations on the active transaction, so percentile math is
 * statistically valid. Existing marks are retained during migration.
 * -------------------------------------------------------------------------- */

/**
 * performance.now() captured at the most recent route start.
 *
 * Initialized to 0 — document `timeOrigin` — so the initial hard pageload has a
 * valid baseline from module load. Svelte mounts children before parents, so the
 * page's `onMount` marks fire before the root layout's; anything that waited for
 * the layout to establish a baseline would miss them.
 */
let routeStartMs = 0;

/** milestone name -> performance.now() at mark time. */
const milestones = new Map<string, number>();

/**
 * Mark the start of a route. Resets per-route milestone state and establishes
 * the baseline for route-relative `route_time_to_*` measurements.
 *
 * Call this from `+layout.svelte` `beforeNavigate` only. SPA navigations are the
 * only case that needs it: hard pageloads use the module-load baseline of 0
 * (`timeOrigin`). Calling it from `onMount` would clear milestones the page's own
 * `onMount` had already recorded, since Svelte mounts children before parents.
 *
 * No-ops when `performance.now` is unavailable.
 * @returns {void}
 */
export function recordRouteStart(): void {
	const now = nowMs();
	if (now == null) return;
	routeStartMs = now;
	milestones.clear();
	stampedPaymentSdks.clear();
	resetPaymentMethodCoordinator();
}

/**
 * Record a one-time milestone relative to the current route start. Emits a
 * User Timing mark (`abc.<name>`), a route-relative Sentry measurement
 * (`time_to_<name>`), and stores the timestamp for later derived durations.
 * Suppresses duplicate milestones and calls made before `recordRouteStart`.
 * @param {string} name - Milestone name (without `abc.` prefix).
 * @returns {void}
 */
export function markMilestone(name: string): void {
	if (!name || milestones.has(name)) return;
	const now = nowMs();
	if (now == null) return;
	milestones.set(name, now);
	userTimingMark(`abc.${name}`);
	sentryMeasurementValue(`route_time_to_${name}`, now - routeStartMs);
}

/**
 * Emit `skeleton_lead`: how long the prerendered skeleton was on screen before
 * the variant painted. Emitted for every visit that painted a variant, clamped
 * at zero, including visits where the skeleton never painted. Dropping those
 * would bias the aggregate upward by counting only the visits the shell helped.
 * @returns {void}
 */
function emitSkeletonLead(): void {
	const painted = milestones.get('variant_painted');
	if (painted == null) return;
	const skeletonVisible = milestones.get('skeleton_visible');
	const lead = skeletonVisible == null ? 0 : Math.max(0, painted - skeletonVisible);
	sentryMeasurementValue('skeleton_lead', lead);
}

/**
 * `performance.now()` when the API is available, otherwise null.
 * @returns {number | null}
 */
function nowMs(): number | null {
	if (typeof performance === 'undefined' || typeof performance.now !== 'function') return null;
	return performance.now();
}

/**
 * Emit a derived duration between two previously-recorded milestones on the
 * active transaction. Suppresses when either milestone is missing or the end
 * precedes the start (misordered). Never emits a negative or zero-from-missing
 * value.
 * @param {string} name - Measurement name (e.g. `skeleton_lead`).
 * @param {string} fromMilestone - Start milestone name.
 * @param {string} toMilestone - End milestone name.
 * @returns {void}
 */
export function measureBetween(name: string, fromMilestone: string, toMilestone: string): void {
	if (!name) return;
	const from = milestones.get(fromMilestone);
	const to = milestones.get(toMilestone);
	if (from == null || to == null) return;
	sentryMeasurementValue(name, to - from);
}
